import { z } from 'zod';
import { callings } from './data/callings.ts';
import { crimes } from './data/crimes.ts';
import { items } from './data/items.ts';
import { masteries } from './data/masteries.ts';
import { minigames } from './data/minigames.ts';
import { routes, towns, venues } from './data/realm.ts';
import { recipes } from './data/recipes.ts';
import { shops } from './data/shops.ts';
import { skills } from './data/skills.ts';
import { ARMOUR_SLOTS, CALLINGS, DISCIPLINES, SKILLS, TOWNS } from './ids.ts';
import { callingSchema, crimeSchema, itemSchema, masterySchema, minigameSchema, recipeSchema, routeSchema, shopSchema, skillSchema, townSchema, venueSchema } from './schema.ts';

export const catalogSchema = z
  .object({
    items: z.array(itemSchema),
    skills: z.array(skillSchema),
    callings: z.array(callingSchema),
    towns: z.array(townSchema),
    venues: z.array(venueSchema),
    routes: z.array(routeSchema),
    crimes: z.array(crimeSchema),
    shops: z.array(shopSchema),
    minigames: z.array(minigameSchema),
    masteries: z.array(masterySchema),
    recipes: z.array(recipeSchema),
  })
  .superRefine((c, ctx) => {
    const issue = (message: string) => ctx.addIssue({ code: 'custom', message });
    const itemKeys = new Set(c.items.map((i) => i.key));
    if (itemKeys.size !== c.items.length) issue('duplicate item keys');
    const ammoKinds = new Set(c.items.flatMap((i) => (i.ammo ? [i.ammo] : [])));
    for (const i of c.items) {
      if (i.kind === 'consumable' && !i.use) issue(`consumable ${i.key} does nothing when used`);
      if (i.use && i.kind !== 'consumable') issue(`item ${i.key} has a use but is not a consumable`);
      if ((i.kind === 'ammo') !== !!i.ammo) issue(`item ${i.key}: ammunition, and only ammunition, names its kind`);
      if (i.tool && i.kind !== 'tool') issue(`item ${i.key} helps a skill but is not a tool`);
      if (!i.slot) continue;
      // The off hand takes a weapon or a shield.
      const shield = i.slot === 'offHand' && i.kind === 'armour';
      const needs = shield || (ARMOUR_SLOTS as readonly string[]).includes(i.slot) ? 'armour' : i.slot === 'mount' ? 'speed' : 'damage';
      if (!i.combat?.[needs]) issue(`item ${i.key} is worn in ${i.slot} but has no ${needs}`);
      if (i.combat?.ammo && !ammoKinds.has(i.combat.ammo)) issue(`item ${i.key} shoots unknown ammo ${i.combat.ammo}`);
    }
    for (const calling of c.callings)
      for (const k of calling.kit) if (!itemKeys.has(k.item)) issue(`calling ${calling.id} kit references unknown item ${k.item}`);
    for (const id of CALLINGS) if (!c.callings.some((x) => x.id === id)) issue(`missing calling ${id}`);
    for (const id of SKILLS) {
      if (!c.skills.some((x) => x.id === id)) issue(`missing skill ${id}`);
      if (c.venues.filter((v) => v.skill === id).length !== 1) issue(`skill ${id} must have exactly one venue`);
    }
    for (const id of TOWNS) if (!c.towns.some((t) => t.id === id)) issue(`missing town ${id}`);
    if (c.towns.filter((t) => t.capital).length !== 1) issue('exactly one capital');
    for (const a of TOWNS)
      for (const b of TOWNS)
        if (a !== b && !c.routes.some((r) => (r.from === a && r.to === b) || (r.from === b && r.to === a)))
          issue(`no road between ${a} and ${b}`);
    if (new Set(c.crimes.map((x) => x.key)).size !== c.crimes.length) issue('duplicate crime keys');
    for (const crime of c.crimes) if (crime.coin.min > crime.coin.max) issue(`crime ${crime.key} coin range is inverted`);
    for (const shop of c.shops)
      for (const s of shop.stock) if (!itemKeys.has(s.item)) issue(`shop ${shop.key} sells unknown item ${s.item}`);
    const venueKeys = new Set(c.venues.map((v) => v.key));
    if (new Set(c.minigames.map((m) => m.venue)).size !== c.minigames.length) issue('a venue has two minigames');
    for (const m of c.minigames) {
      if (!venueKeys.has(m.venue)) issue(`minigame for unknown venue ${m.venue}`);
      if (new Set(m.activities.map((a) => a.key)).size !== m.activities.length) issue(`duplicate activity keys at ${m.venue}`);
      for (const a of m.activities) {
        const made = 'ingot' in a ? a.ingot : 'log' in a ? a.log : 'catch' in a ? a.catch : 'product' in a ? a.product : null;
        for (const k of [made, ...(a.inputs ?? []).map((i) => i.item)]) if (k !== null && !itemKeys.has(k)) issue(`activity ${m.venue}/${a.key} references unknown item ${k}`);
        if ('logs' in a && a.logs[0] > a.logs[1]) issue(`activity ${m.venue}/${a.key} log range is inverted`);
        for (const f of ['loot', 'distance', 'reserve'] as const)
          if (f in a && (a as Record<string, unknown>)[f] instanceof Array) {
            const [lo, hi] = (a as Record<string, unknown>)[f] as [number, number];
            if (lo > hi) issue(`activity ${m.venue}/${a.key} ${f} range is inverted`);
          }
      }
    }
    for (const d of ['martial', 'craft', 'cunning', 'cloister'] as const)
      if (c.skills.filter((s) => s.discipline === d).length !== 4) issue(`discipline ${d} needs 4 skills`);
    for (const d of DISCIPLINES) {
      const tiers = c.masteries.filter((m) => m.discipline === d).sort((a, b) => a.tier - b.tier);
      if (tiers.map((m) => m.tier).join() !== '1,2,3,4') issue(`discipline ${d} needs mastery tiers 1 to 4`);
      if (tiers.some((m, i) => i > 0 && m.levels <= tiers[i - 1]!.levels)) issue(`discipline ${d} mastery levels must rise with the tier`);
      if (tiers.some((m) => m.levels > 400)) issue(`discipline ${d} has a tier past four skills at 100`);
    }
    if (new Set(c.masteries.map((m) => m.title)).size !== c.masteries.length) issue('duplicate mastery titles');
    if (new Set(c.recipes.map((r) => r.key)).size !== c.recipes.length) issue('duplicate recipe keys');
    for (const r of c.recipes) {
      if (!venueKeys.has(r.venue)) issue(`recipe ${r.key} is made at unknown venue ${r.venue}`);
      if (new Set(r.skills.map((x) => x.skill)).size !== r.skills.length) issue(`recipe ${r.key} lists a skill twice`);
      for (const k of [r.product.item, ...r.inputs.map((i) => i.item)]) if (!itemKeys.has(k)) issue(`recipe ${r.key} references unknown item ${k}`);
    }
  });

export type Catalog = z.output<typeof catalogSchema>;

export const rawCatalog = { items, skills, callings, towns, venues, routes, crimes, shops, minigames, masteries, recipes };
